import React, { useState, useRef, useEffect } from "react";

/**
 * App-styled dropdown replacing native <select>. Supports optional in-menu search
 * and folder-style grouping (options may carry a `group` label — rendered as a
 * header, like the "Add to sequence" picker on Contacts).
 *
 * Props: value, options [{value, label, group?}], placeholder, onChange, disabled,
 * searchable, searchPlaceholder.
 */
const AgentSelect = ({
  value,
  options = [],
  placeholder = "Select…",
  onChange = () => {},
  disabled = false,
  searchable = false,
  searchPlaceholder = "Search…"
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open && searchable && inputRef.current) inputRef.current.focus();
  }, [open, searchable]);

  const selected = options.find(o => o.value === value);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(
        o =>
          String(o.label || "").toLowerCase().includes(q) ||
          String(o.group || "").toLowerCase().includes(q)
      )
    : options;

  const hasGroups = filtered.some(o => o.group);
  const groups = [];
  const byGroup = {};
  filtered.forEach(o => {
    const g = o.group || "";
    if (!byGroup[g]) {
      byGroup[g] = [];
      groups.push(g);
    }
    byGroup[g].push(o);
  });

  const pick = v => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const renderOpt = o => (
    <div
      key={o.value}
      className={`ag-select__opt ${o.value === value ? "is-sel" : ""}`}
      // Use mousedown + preventDefault so selecting closes reliably even when the
      // onChange re-renders the surrounding form (e.g. changing the trigger type).
      onMouseDown={e => { e.preventDefault(); pick(o.value); }}
    >
      {o.label}
    </div>
  );

  return (
    <div className={`ag-select ${disabled ? "is-disabled" : ""}`} ref={ref}>
      <button
        type="button"
        className={`ag-select__btn ${open ? "is-open" : ""}`}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
      >
        <span className={selected ? "" : "ag-select__ph"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="ag-select__menu">
          {searchable && (
            <div className="ag-select__search">
              <input
                ref={inputRef}
                className="ag-select__searchInput"
                placeholder={searchPlaceholder}
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
          )}
          <div className="ag-select__list">
            {filtered.length === 0 && <div className="ag-select__empty">No matches</div>}
            {!hasGroups && filtered.map(renderOpt)}
            {hasGroups &&
              groups.map(g => (
                <div key={g || "_"} className="ag-select__grp">
                  <div className="ag-select__grpHead">{g || "Other"}</div>
                  {byGroup[g].map(renderOpt)}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentSelect;
